import { isNil } from 'lodash';
import type { Portfolio } from 'venn-api';

/* Allocation constraint types */

export type AllocationItem = Pick<Portfolio, 'id' | 'name' | 'fund' | 'allocation' | 'draft'>;
export type AllocationCondition = 'maxTurnover' | 'minAllocation' | 'maxAllocation' | 'lockedAllocation';

export type ConstraintValueType = 'percentOfPortfolio' | 'fixedValue' | 'currentValue';

export interface AllocConstraint {
  items: AllocationItem[];
  allInvestments?: boolean;
  condition: AllocationCondition;
  valueType: ConstraintValueType;
  value?: number;
  isInPortfolioPolicy: boolean;
  isNew?: boolean;
}

/* Factor exposure constraint types */

export type FactorCondition = 'minExposure' | 'maxExposure';

export interface FactorExposureConstraint {
  factorId: number;
  condition: FactorCondition;
  value: number;
  isInPortfolioPolicy: boolean;
}

/* Relative-To-Benchmark constraint type */
export interface BenchmarkConstraint {
  benchmarkFundId?: string;
  benchmarkStrategyId?: number;
  benchmarkName?: string;
  maxTrackingError: number | undefined; // decimal (0.05 = 5%), undefined when not yet entered
  isInPortfolioPolicy: boolean;
}

// A constraint with a benchmark id set drives the optimization request (see currentPolicyWithConstraints
// in useConstraints.ts) as soon as it's saved, regardless of isInPortfolioPolicy - so anything defaulting
// off of it should key off the same thing rather than waiting for the separate "Apply to Policy" action.
export const hasAppliedBenchmark = (constraint: BenchmarkConstraint | undefined): boolean =>
  !isNil(constraint?.benchmarkStrategyId) || !isNil(constraint?.benchmarkFundId);

// A relative-to-benchmark constraint has no effect while maximizing Sharpe: useOptimization.ts's
// getRequest strips the tracking-error constraint and omits the benchmark from the request entirely
// in that mode, so nothing that defaults off the constraint should treat it as active either -
// otherwise a constraint set under a different objective would keep "sticking" after switching.
export const isBenchmarkConstraintActive = (
  constraint: BenchmarkConstraint | undefined,
  objective: ObjectiveType | undefined,
): boolean => hasAppliedBenchmark(constraint) && objective !== 'maximizeSharpe';

// Identifies which benchmark a constraint currently points at (or undefined if it doesn't point at
// one, including while maximizing Sharpe). A benchmark/toggle default that was overridden by hand
// should go back to tracking the constraint whenever this key changes (constraint's benchmark
// changed, was removed, or the objective switched to/from maximizeSharpe) - only a steady key
// should let a manual override stick.
export const getAppliedBenchmarkKey = (
  constraint: BenchmarkConstraint | undefined,
  objective: ObjectiveType | undefined,
): string | undefined =>
  isBenchmarkConstraintActive(constraint, objective)
    ? `${constraint?.benchmarkStrategyId ?? ''}:${constraint?.benchmarkFundId ?? ''}`
    : undefined;

/* Objective types */

export type ObjectiveType = 'maximizeReturns' | 'targetReturn' | 'maximizeSharpe';
